import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowDownUp, ChevronDown, ChevronUp, CircleHelp, Clock, Loader2, Pencil, Trash2, X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

/**
 * SAB-1017: the pending waiver claims a manager has placed, with cancel.
 *
 * Until now a bid vanished the moment it was submitted — no confirmation it existed, no way
 * to withdraw it, and no sight of the FAAB it had committed until the claim resolved
 * overnight. On a money surface that is the same class of gap as the failed winner who was
 * told nothing (SAB-1027).
 *
 * Reads through nfl-add-drop's `get_status`, which already enriches claims with player and
 * drop names, rather than querying nfl_waiver_claims directly: the edge function owns the
 * ownership check, and a second read path would be a second place for that to be wrong.
 *
 * ── Waiver panel UX cleanup (design handoff) ──────────────────────────────────────────
 *
 * The block read as an undifferentiated wall of small grey text. It is now a COLLAPSIBLE
 * SUMMARY: one row answering "what's queued, how much FAAB is free, when does it run",
 * expanding to the full claim manager. No action was added or removed — reorder, bid edit
 * and cancel go through exactly the same calls they did before.
 */

interface PendingClaim {
  id: string;
  /** SAB-1039: the manager's own stack rank. Null on pre-1039 rows; sorts last. */
  claim_rank?: number | null;
  player_name?: string | null;
  player_position?: string | null;
  drop_player_name?: string | null;
  faab_bid: number | string;
  status: string;
  /**
   * SAB-1142b: WHEN this claim resolves, read from the column the job filters on.
   * Never recomputed here. Deriving a clear time client-side is the SAB-1140 defect --
   * the copy and the behaviour get separate definitions and drift apart with neither
   * looking wrong.
   */
  process_at?: string | null;
  /** played_this_week (weekly) | recent_drop | post_draft (both nightly). */
  waiver_reason?: string | null;
  processed_at?: string | null;
  failure_code?: string | null;
}

/**
 * "Processes Wed 2am ET" — rendered from the stored instant, in ET.
 *
 * ET rather than the viewer's zone on purpose, and labelled as such: the waiver run is a
 * league-wide event at a fixed ET hour, so every manager in the league should read the same
 * words. (The list BADGE renders a clear day in the viewer's zone because that answers a
 * different question — "when can I have him" — and SAB-1045 settled that.)
 *
 * Returns null rather than a guess when the instant is missing: a wrong processing time is
 * worse than none, and a claim written before this column existed genuinely has no answer.
 */
function processesAtLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', weekday: 'short', hour: 'numeric', hourCycle: 'h23',
  }).formatToParts(d);
  const wd = parts.find(p => p.type === 'weekday')?.value ?? '';
  const hr = Number(parts.find(p => p.type === 'hour')?.value ?? '0');
  const h12 = hr % 12 === 0 ? 12 : hr % 12;
  return `Processes ${wd} ${h12}${hr < 12 ? 'am' : 'pm'} ET`;
}

interface ProcessedClaim {
  id: string;
  player_name?: string | null;
  player_position?: string | null;
  faab_bid: number | string;
  status: string;
  failure_code?: string | null;
  processed_at?: string | null;
}

interface Outcome {
  /** Unchanged strings. */
  text: string;
  /** Tailwind text colour for the label. */
  tone: string;
  /** Tailwind background for the status dot's tinted wrapper. */
  dotBg: string;
  /** Tailwind background for the dot itself. */
  dot: string;
}

/**
 * Plain-language outcome. Never invents a reason a failure code does not carry.
 *
 * The redesign added a TONE alongside the text; every string is byte-identical to what it
 * returned before, because the words are what the manager was told by email (SAB-1028) and
 * two surfaces describing one result differently is its own defect.
 */
function outcomeLabel(c: ProcessedClaim): Outcome {
  const gold = { tone: 'text-gold', dotBg: 'bg-gold/15', dot: 'bg-gold' };
  if (c.status === 'awarded') {
    return { text: 'Won', tone: 'text-success-glow', dotBg: 'bg-success/20', dot: 'bg-success-glow' };
  }
  if (c.status === 'outbid') {
    return { text: 'Outbid', tone: 'text-damage', dotBg: 'bg-damage/15', dot: 'bg-damage' };
  }
  if (c.status === 'cancelled') {
    return {
      text: 'Cancelled',
      tone: 'text-muted-foreground/70', dotBg: 'bg-muted-foreground/20', dot: 'bg-muted-foreground/70',
    };
  }
  if (c.status === 'failed') {
    // FAAB_INSUFFICIENT is the SAB-1147 case: pledged more across claims than the budget.
    if (c.failure_code === 'FAAB_INSUFFICIENT') return { text: 'Not enough FAAB left', ...gold };
    if (c.failure_code === 'NO_ROSTER_SLOT') return { text: 'No roster slot', ...gold };
    return { text: c.failure_code ? `Failed — ${c.failure_code}` : 'Failed', ...gold };
  }
  return {
    text: c.status,
    tone: 'text-muted-foreground/70', dotBg: 'bg-muted-foreground/20', dot: 'bg-muted-foreground/70',
  };
}

interface Props {
  teamId: string | null;
  /**
   * The team's REMAINING FAAB — `nfl_teams.faab_budget`, which despite its name is a
   * balance: nfl_debit_faab DECREMENTS it at award (SAB-748) and names its own variable
   * `v_remaining`. NFL stores no separate season budget and no `faab_remaining` column, so
   * this is the only FAAB figure that exists for a team.
   *
   * Passed in from My Team, which already holds it — the handoff is explicit that the panel
   * adds no query of its own. Undefined/null means "not known", and the meter and header
   * readout are OMITTED rather than defaulted: a made-up budget on a money surface is the
   * same failure as a made-up processing time.
   */
  faabRemaining?: number | null;
}

export default function NFLPendingClaims({ teamId, faabRemaining }: Props) {
  const { toast } = useToast();
  const [claims, setClaims] = useState<PendingClaim[]>([]);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  /** SAB-1142b: id of the claim whose bid is being edited, and the draft value. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBid, setEditBid] = useState('');
  const [savingBid, setSavingBid] = useState(false);
  const [failed, setFailed] = useState(false);
  /** Panel expansion. Collapsed by default — the summary row is the point. */
  const [open, setOpen] = useState(false);
  /** The rules disclosure. Local, unpersisted. */
  const [helpOpen, setHelpOpen] = useState(false);
  /**
   * SAB-1142b: this team's RECENTLY PROCESSED claims.
   *
   * Read from the SAME get_status payload as the pending list — get_status already returns
   * this team's last claims of any status, scoped by team_id, carrying processed_at and
   * failure_code. No second query.
   *
   * Deliberately NOT nfl_waiver_results, despite that being the league-wide authority: it
   * exposes team_NAME and no team_id, so filtering it to one team means matching on a
   * string nothing guarantees is unique within a league. An exact team_id scope beats a
   * shared-source argument when the shared source cannot express the identity.
   *
   * It exists because a claim used to LEAVE this panel with no local trace when it
   * resolved. The manager got a bell and an email (SAB-1028), but coming back to My Team
   * showed an empty panel and no record — worst for a claim that failed FAAB_INSUFFICIENT,
   * which is exactly when an explanation is most wanted (SAB-1147).
   */
  const [processed, setProcessed] = useState<ProcessedClaim[]>([]);

  const call = useCallback(async (body: Record<string, unknown>) => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(
      `${(supabase as unknown as { supabaseUrl: string }).supabaseUrl}/functions/v1/nfl-add-drop`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    return { ok: res.ok, data: await res.json() };
  }, []);

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const { ok, data } = await call({ action: 'get_status', team_id: teamId });
      if (!ok) { setFailed(true); return; }
      setFailed(false);
      // Only PENDING claims belong in the manager. A resolved claim is history and shows
      // read-only in the processed tail; giving one a Cancel button would offer an action
      // the server refuses ("Claim is no longer pending").
      // SAB-1039: ordered by the manager's OWN rank, not creation order. Null sorts last so
      // an unranked legacy claim cannot appear above one they deliberately placed.
      const pending = ((data.claims ?? []) as PendingClaim[]).filter(c => c.status === 'pending');
      pending.sort((a, b) =>
        (a.claim_rank ?? Number.MAX_SAFE_INTEGER) - (b.claim_rank ?? Number.MAX_SAFE_INTEGER));
      setClaims(pending);

      const done = ((data.claims ?? []) as ProcessedClaim[])
        .filter(c => c.status !== 'pending')
        .slice(0, 5);
      setProcessed(done);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [teamId, call]);

  useEffect(() => { void load(); }, [load]);

  /**
   * SAB-1039: move one claim up or down, then send the FULL new order.
   *
   * A full list rather than a "move X to position N" delta: a delta has to be applied
   * against the order the client last saw, and if a claim resolved in between, client and
   * server disagree about what position N means. The full list is idempotent — whatever it
   * says, that is the order.
   *
   * Optimistic locally so the row moves under the finger, then reconciled from the server.
   * If the RPC refuses, the reload puts it back rather than leaving the UI asserting an
   * order the server does not hold.
   */
  const move = async (id: string, dir: -1 | 1) => {
    const i = claims.findIndex(c => c.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= claims.length) return;
    const next = [...claims];
    [next[i], next[j]] = [next[j], next[i]];
    setClaims(next);
    setReordering(true);
    try {
      const { error } = await (supabase as unknown as {
        rpc: (fn: string, args: unknown) => Promise<{ error: { message: string } | null }>;
      }).rpc('nfl_reorder_waiver_claims', {
        p_team_id: teamId,
        p_claim_ids: next.map(c => c.id),
      });
      if (error) {
        toast({ title: 'Could not reorder', description: error.message, variant: 'destructive' });
      }
      await load();
    } finally {
      setReordering(false);
    }
  };

  /**
   * SAB-1142b: change a pending claim's bid.
   *
   * Goes through nfl-add-drop rather than a table write: nfl_waiver_claims is no longer
   * client-writable (SAB-1142 revoked INSERT/UPDATE/DELETE from authenticated), and the
   * server re-validates the ceiling — an edit that skipped it could set a bid above budget
   * that then fails at award hours later.
   *
   * The schedule is deliberately NOT resent. Changing a bid must not move a claim from the
   * weekly run to tonight's, so process_at is decided once at submission and left alone.
   */
  const saveBid = async (id: string) => {
    const n = Number(editBid);
    if (!Number.isInteger(n) || n < 0) {
      toast({ title: 'Enter a whole dollar amount', description: '$0 is allowed.', variant: 'destructive' });
      return;
    }
    setSavingBid(true);
    try {
      const { ok, data } = await call({ action: 'update_claim_bid', claim_id: id, bid_amount: n });
      if (!ok || data.error) {
        toast({ title: 'Could not update bid', description: data.error || 'Please try again.', variant: 'destructive' });
        return;
      }
      setEditingId(null);
      await load();
    } finally {
      setSavingBid(false);
    }
  };

  const cancel = async (id: string) => {
    setCancelling(id);
    try {
      const { ok, data } = await call({ action: 'cancel_claim', claim_id: id });
      if (!ok || data.error) {
        toast({ title: 'Could not cancel', description: data.error || 'Please try again.', variant: 'destructive' });
        return;
      }
      // Refetch rather than splicing locally: the claim may already have resolved, and the
      // server's view is the one that decides.
      await load();
      toast({ title: 'Claim cancelled', description: 'Your FAAB is free again.' });
    } finally {
      setCancelling(null);
    }
  };

  const committed = useMemo(
    () => claims.reduce((a, c) => a + (Number(c.faab_bid) || 0), 0),
    [claims],
  );
  const balanceKnown = typeof faabRemaining === 'number' && Number.isFinite(faabRemaining);
  const balance = balanceKnown ? Math.max(0, faabRemaining as number) : null;
  const free = balance === null ? null : Math.max(0, balance - committed);
  const pct = balance === null || balance <= 0 ? 0 : Math.min(100, (committed / balance) * 100);

  /**
   * The header's "Next run …", from the EARLIEST pending claim.
   *
   * Sorted on the process_at INSTANT, never on the rendered string: the label is display
   * copy whose lexical order is meaningless — "Wed" sorts before "tonight" — so picking the
   * soonest run by comparing words would confidently name the wrong one. Claims with no
   * instant are skipped rather than sorted to either end, and if none has one the sub-line
   * is omitted entirely.
   */
  const nextRunLabel = useMemo(() => {
    let best: { t: number; iso: string } | null = null;
    for (const c of claims) {
      if (!c.process_at) continue;
      const t = new Date(c.process_at).getTime();
      if (Number.isNaN(t)) continue;
      if (!best || t < best.t) best = { t, iso: c.process_at };
    }
    if (!best) return null;
    const label = processesAtLabel(best.iso);
    return label ? label.replace('Processes ', 'Next run ') : null;
  }, [claims]);

  if (!teamId) return null;

  // A failed read says so rather than rendering empty — and says it OUTSIDE the collapse,
  // because a card that hides its own failure behind a chevron is the same defect with an
  // extra click. An empty list and a failed read look identical to a manager, and one of
  // them means their bid is still live.
  if (failed) {
    return (
      <Card className="border-border bg-card p-[14px]" data-testid="pending-claims">
        <p className="text-[12px] text-muted-foreground">
          Your claims are unavailable right now — they have not been cancelled.
        </p>
      </Card>
    );
  }

  if (loading && claims.length === 0) {
    return (
      <Card className="border-border bg-card p-[14px]" data-testid="pending-claims">
        <p className="text-[12px] text-muted-foreground">Loading…</p>
      </Card>
    );
  }

  // VISIBILITY: nothing pending, no panel.
  //
  // This deliberately REVERSES the SAB-1142b emptiness test, which also kept the card alive
  // for the processed tail alone. That was the right call for the defect it fixed — a claim
  // used to resolve and leave no trace — but it means every manager carries a card above
  // their lineup for days after the last claim settled. The tail now rides along with a live
  // claim instead of keeping an otherwise-empty card on the page. A product decision, not an
  // oversight: the outcome of a resolved claim still reaches the manager by bell and email
  // (SAB-1028) and lives in the league-wide results log.
  if (claims.length === 0) return null;

  return (
    <Card
      className="overflow-hidden rounded-[12px] border-border bg-card p-0 shadow-[var(--shadow-sm)]"
      data-testid="pending-claims"
    >
      {/* ── Collapsed summary: what's queued, what's free, when it runs ───────────── */}
      <button
        type="button"
        data-testid="waiver-panel-toggle"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className={cn(
          'flex w-full min-h-[56px] items-center gap-[12px] px-[14px] py-[12px] text-left',
          'transition-colors hover:bg-secondary/60',
        )}
      >
        <span
          className={cn(
            'flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px]',
            'border border-primary/30 bg-primary/15',
          )}
        >
          <ArrowDownUp className="h-[16px] w-[16px] text-primary" />
        </span>

        <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <span className="flex items-center gap-[8px]">
            <span className="font-display text-[15px] font-bold tracking-[-0.01em] text-foreground">
              Waivers
            </span>
            <span
              className={cn(
                'inline-flex h-[20px] shrink-0 items-center rounded-full bg-primary px-[8px]',
                'font-display text-[11px] font-bold text-primary-foreground',
              )}
            >
              {claims.length} pending
            </span>
          </span>
          {/* Omitted entirely when no pending claim carries an instant — never guessed. */}
          {nextRunLabel && (
            <span data-testid="waiver-next-run" className="truncate text-[11.5px] text-muted-foreground">
              {nextRunLabel}
            </span>
          )}
        </span>

        {/* Omitted when the balance is unknown, for the same reason. */}
        {free !== null && (
          <span className="flex shrink-0 flex-col items-end gap-[2px]">
            <span className="font-display text-[15px] font-bold tabular-nums text-foreground">
              ${free}
            </span>
            <span className="text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground/70">
              FAAB free
            </span>
          </span>
        )}

        <span className="flex h-[28px] w-[28px] shrink-0 items-center justify-center">
          <ChevronDown
            className={cn(
              'h-[16px] w-[16px] text-muted-foreground transition-transform duration-[160ms] ease-out',
              open && 'rotate-180',
            )}
          />
        </span>
      </button>

      {open && (
        <div className="px-[14px] pb-[14px]">
          {/* ── FAAB meter ───────────────────────────────────────────────────────── */}
          {balance !== null && (
            <div
              data-testid="faab-meter"
              className="flex flex-col gap-[8px] rounded-[10px] border border-secondary bg-background p-[12px]"
            >
              <div className="flex items-center justify-between gap-[8px]">
                <span className="text-[12px] text-muted-foreground">Committed on pending claims</span>
                <span className="font-display text-[13px] font-bold tabular-nums text-primary">
                  ${committed}
                </span>
              </div>
              <div className="h-[6px] w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    'h-full rounded-full bg-[image:var(--gradient-sabotage)]',
                    'transition-[width] duration-200 ease-out',
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="flex items-center justify-between gap-[8px] text-[11.5px] text-muted-foreground/70">
                <span className="tabular-nums">${free} free to bid</span>
                {/* NFL stores no season budget — see the faabRemaining prop. This names the
                    figure we actually hold rather than asserting a season total we do not. */}
                <span className="tabular-nums">${balance} balance</span>
              </div>
            </div>
          )}

          {/* ── Section header + help toggle ─────────────────────────────────────── */}
          <div className="mt-[14px] flex items-center justify-between gap-[8px]">
            <span className="font-display text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
              Your claim order
            </span>
            <button
              type="button"
              data-testid="waiver-help-toggle" data-compact
              aria-expanded={helpOpen}
              onClick={() => setHelpOpen(h => !h)}
              className={cn(
                '-my-[4px] -mr-[8px] flex items-center gap-[4px] rounded-[8px] px-[8px] py-[4px] text-[11.5px]',
                'text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground',
              )}
            >
              <CircleHelp className="h-[13px] w-[13px]" />
              How waivers work
            </button>
          </div>

          {helpOpen && (
            <p
              data-testid="waiver-help"
              className={cn(
                'mt-[8px] rounded-[10px] border border-secondary bg-secondary px-[12px] py-[10px]',
                'text-[12px] leading-[1.5] text-muted-foreground [text-wrap:pretty]',
              )}
            >
              {/* Copy unchanged. It said "Claims process daily at ..." until SAB-1142b, which
                  stopped being true when played-this-week claims moved to a weekly run; each
                  row now states its own time from its stored schedule. */}
              Each claim shows when it processes. Highest bid wins; ties go to the team lowest
              in the standings. Your order decides which of YOUR claims gets your budget and
              roster spots first — it does not outbid another team.
            </p>
          )}

          {/* ── Claim rows ──────────────────────────────────────────────────────── */}
          <div className="mt-[10px] flex flex-col gap-[8px]">
            {claims.map((c, i) => {
              const when = processesAtLabel(c.process_at);
              const bid = Number(c.faab_bid) || 0;
              return (
                <div
                  key={c.id}
                  data-testid="pending-claim"
                  className={cn(
                    'flex items-stretch gap-[10px] rounded-[10px] border border-border bg-background/60 p-[10px]',
                    'transition-colors hover:border-muted-foreground/60',
                  )}
                >
                  {/* Stepper: chevrons with the rank BETWEEN them. Up/down rather than drag —
                      this list is short, the controls work identically on a phone and a
                      desktop, and a drag handle on a small row is the affordance most likely
                      to fire on a scroll. */}
                  <div className="flex w-[30px] shrink-0 flex-col items-center gap-[2px]">
                    <button
                      type="button" data-testid="claim-up" data-compact
                      aria-label={`Move ${c.player_name || 'claim'} up`}
                      disabled={i === 0 || reordering}
                      onClick={() => move(c.id, -1)}
                      className={cn(
                        'flex h-[22px] w-[30px] items-center justify-center rounded-[6px]',
                        'border border-border bg-secondary text-foreground',
                        'transition-colors hover:border-muted-foreground/60 disabled:opacity-30',
                      )}
                    >
                      <ChevronUp className="h-[13px] w-[13px]" strokeWidth={2.5} />
                    </button>
                    <span className="font-display text-[13px] font-extrabold leading-none tabular-nums text-foreground">
                      {i + 1}
                    </span>
                    <button
                      type="button" data-testid="claim-down" data-compact
                      aria-label={`Move ${c.player_name || 'claim'} down`}
                      disabled={i === claims.length - 1 || reordering}
                      onClick={() => move(c.id, 1)}
                      className={cn(
                        'flex h-[22px] w-[30px] items-center justify-center rounded-[6px]',
                        'border border-border bg-secondary text-foreground',
                        'transition-colors hover:border-muted-foreground/60 disabled:opacity-30',
                      )}
                    >
                      <ChevronDown className="h-[13px] w-[13px]" strokeWidth={2.5} />
                    </button>
                  </div>

                  <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
                    <div className="flex min-w-0 flex-wrap items-center gap-[6px]">
                      {/* WRAPS, not truncated. The handoff specifies truncation for the
                          processed tail and the opposite principle for these rows, and at
                          320pt every name here truncates — "Jaylen Wa…" on the row that
                          commits real FAAB. The processed tail still truncates, as spec'd. */}
                      <span className="min-w-0 break-words text-[13.5px] font-semibold text-foreground">
                        {c.player_name || 'Unknown player'}
                      </span>
                      {c.player_position && (
                        <span
                          className={cn(
                            'shrink-0 rounded-[4px] bg-muted px-[5px] py-[1px]',
                            'font-display text-[10px] font-bold tracking-[0.04em] text-muted-foreground',
                          )}
                        >
                          {c.player_position}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[4px] text-[11px] text-muted-foreground">
                      {/* Omitted WHOLESALE when process_at is null — a claim written before
                          the column existed genuinely has no answer, and a wrong processing
                          time is worse than none. */}
                      {when && (
                        <span data-testid="claim-processes-at" className="flex items-center gap-[4px]">
                          <Clock className="h-[11px] w-[11px] shrink-0" />
                          {when}
                        </span>
                      )}
                      {c.drop_player_name && (
                        <span className="flex min-w-0 items-start gap-[4px] text-damage">
                          <Trash2 className="mt-[2px] h-[11px] w-[11px] shrink-0" />
                          {/* WRAPS rather than truncating. `truncate` is nowrap + overflow
                              hidden, which at 320pt hides the back half of a long name —
                              the exact behaviour the handoff's mobile section rules out. */}
                          <span className="min-w-0 break-words">Dropping {c.drop_player_name}</span>
                        </span>
                      )}
                      {c.waiver_reason === 'played_this_week' && (
                        <span
                          className={cn(
                            'min-w-0 rounded-full border border-primary/30 bg-primary/15 px-[6px] py-[1px]',
                            'text-[10px] text-sabotage-glow',
                          )}
                        >
                          Played this week
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-[6px]">
                    {editingId === c.id ? (
                      <span
                        className={cn(
                          'flex items-center gap-[4px] rounded-[10px] border border-primary bg-background',
                          'py-[3px] pl-[8px] pr-[4px]',
                        )}
                      >
                        <span className="font-display text-[13px] font-bold text-muted-foreground">$</span>
                        <input
                          data-testid="edit-bid-input"
                          type="number" min={0} inputMode="numeric"
                          value={editBid}
                          autoFocus
                          onChange={(e) => setEditBid(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void saveBid(c.id);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          className={cn(
                            'h-[26px] w-[46px] border-0 bg-transparent p-0 text-right',
                            'font-display text-[14px] font-bold tabular-nums text-foreground',
                            'focus:outline-none focus:ring-0',
                          )}
                          aria-label={`New bid for ${c.player_name || 'player'}`}
                        />
                        <Button
                          data-testid="edit-bid-save" size="sm" variant="ghost" data-compact
                          className={cn(
                            'h-[26px] shrink-0 rounded-[6px] bg-success px-[8px]',
                            'font-display text-[11.5px] font-bold text-white hover:bg-success/90',
                          )}
                          disabled={savingBid}
                          onClick={() => void saveBid(c.id)}
                        >
                          {savingBid ? <Loader2 className="h-[14px] w-[14px] animate-spin" /> : 'Save'}
                        </Button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        data-testid="edit-bid"
                        data-compact
                        aria-label={`Change bid for ${c.player_name || 'player'}, currently $${bid}`}
                        onClick={() => { setEditingId(c.id); setEditBid(String(bid)); }}
                        className={cn(
                          'flex h-[32px] items-center gap-[4px] rounded-[10px] border border-primary/35 bg-primary/15 px-[10px]',
                          'font-display text-[14px] font-bold tabular-nums text-sabotage-glow',
                          'transition-colors hover:border-primary hover:bg-primary/25',
                        )}
                      >
                        ${bid}
                        <Pencil className="h-[11px] w-[11px] opacity-75" />
                      </button>
                    )}
                    <Button
                      data-testid="cancel-claim"
                      data-compact
                      size="sm" variant="ghost"
                      className={cn(
                        'h-[32px] w-[32px] shrink-0 rounded-[10px] border border-border bg-transparent p-0',
                        'text-muted-foreground/70 hover:border-damage/50 hover:bg-damage/10 hover:text-damage',
                      )}
                      aria-label={`Cancel claim on ${c.player_name || 'player'}`}
                      disabled={cancelling === c.id}
                      onClick={() => cancel(c.id)}
                    >
                      {cancelling === c.id
                        ? <Loader2 className="h-[14px] w-[14px] animate-spin" />
                        : <X className="h-[14px] w-[14px]" />}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Recently processed ──────────────────────────────────────────────── */}
          {processed.length > 0 && (
            <div data-testid="processed-claims" className="mt-[16px] border-t border-border pt-[12px]">
              <div className="flex items-center justify-between gap-[8px]">
                <span className="font-display text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                  Recently processed
                </span>
                <span className="text-[11px] text-muted-foreground/70">
                  Last {processed.length} claim{processed.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="mt-[4px] flex flex-col">
                {processed.map(c => {
                  const o = outcomeLabel(c);
                  return (
                    <div
                      key={c.id}
                      data-testid="processed-claim"
                      className={cn(
                        'flex items-center gap-[10px] rounded-[8px] px-[8px] py-[7px]',
                        'transition-colors hover:bg-background/60',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full',
                          o.dotBg,
                        )}
                      >
                        <span className={cn('h-[6px] w-[6px] rounded-full', o.dot)} />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-[12.5px] text-muted-foreground">
                        {c.player_name || 'Unknown player'}
                        {c.player_position ? ` · ${c.player_position}` : ''}
                      </span>
                      <span className="shrink-0 font-display text-[12px] font-bold tabular-nums text-muted-foreground/70">
                        ${Number(c.faab_bid) || 0}
                      </span>
                      <span
                        data-testid="processed-outcome"
                        className={cn('shrink-0 text-right text-[11.5px] font-semibold', o.tone)}
                      >
                        {o.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
